import { Link, Navigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Sparkles, BookOpen, Music, Star, ChevronRight, TreeDeciduous, Mail, BookText, PawPrint, HandHeart, Salad, Headphones, Palette, Leaf, Download, Check, Sun, Moon, Smartphone, Share, Plus, MoreVertical, MessageSquare, Users, Library, NotebookPen } from "lucide-react";

const Landing = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, loading } = useAuth();
  
  // If user is already logged in, redirect to dashboard
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Show nothing while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Full mobile viewport */}
      <section className="relative min-h-[100dvh] flex flex-col overflow-hidden">
        {/* Background Image - Lush green forest (light mode) / Moody misty forest (dark mode) */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700"
          style={{
            backgroundImage: `url('${isDark 
              ? 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=85' 
              : 'https://images.unsplash.com/photo-1511497584788-876760111969?w=1600&q=85'}')`,
          }}
        >
          {/* Gradient overlay - adapts to theme for legibility */}
          <div className={`absolute inset-0 ${isDark
            ? 'bg-gradient-to-b from-background/75 via-background/60 to-background/95'
            : 'bg-gradient-to-b from-background/55 via-background/35 to-background/85'}`}></div>
        </div>
        
        {/* Nav */}
        <nav className="relative p-4 sm:p-6 md:p-8 flex justify-between items-center z-10">
          <Link to="/" className="flex items-center">
            {/* Light Mode: Show Logo */}
            {!isDark && (
              <img 
                src="https://customer-assets.emergentagent.com/job_4d1c54ed-139e-41a2-9c35-4d75dfddc66c/artifacts/cxzvcxai_Subheading.png" 
                alt="Lala Land Logo" 
                className="h-12 sm:h-14 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            )}
            {/* Dark Mode: Glowing Leaf with Text */}
            {isDark && (
              <div className="flex items-center gap-2 group">
                <div className="relative">
                  <Leaf 
                    className="h-8 sm:h-10 w-8 sm:w-10 text-emerald-400 transition-all duration-500 group-hover:scale-110"
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(52, 211, 153, 0.8)) drop-shadow(0 0 16px rgba(52, 211, 153, 0.6)) drop-shadow(0 0 24px rgba(52, 211, 153, 0.4))'
                    }}
                  />
                  <div className="absolute inset-0 bg-emerald-400/40 blur-xl rounded-full animate-pulse"></div>
                </div>
                <span 
                  className="text-lg sm:text-xl font-semibold text-white tracking-wide"
                  style={{
                    textShadow: '0 0 10px rgba(52, 211, 153, 0.6), 0 0 20px rgba(52, 211, 153, 0.4)'
                  }}
                >
                  Lala Land
                </span>
              </div>
            )}
          </Link>
          <div className="flex gap-2 sm:gap-4 items-center">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full h-9 w-9"
              data-testid="landing-theme-toggle"
            >
              {isDark ? <Sun className="h-4 w-4 sm:h-5 sm:w-5" /> : <Moon className="h-4 w-4 sm:h-5 sm:w-5" />}
            </Button>
            <Link to="/about" className="text-primary hover:text-primary/80 transition-colors font-medium hidden md:block">
              Our Story
            </Link>
            <Link to="/login" className="hidden sm:block">
              <Button variant="ghost" className="text-primary hover:bg-primary/5" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-primary text-primary-foreground rounded-full px-4 sm:px-6 text-sm sm:text-base hover:bg-primary/90" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </nav>

        {/* Hero Content - Centered with glassmorphism card for readability */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 max-w-4xl mx-auto animate-fade-in py-8">
          {/* Glassmorphism card for text readability */}
          <div className="bg-background/80 dark:bg-background/85 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-10 shadow-xl border border-primary/10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-muted-foreground mb-4 sm:mb-6 font-medium">
              by Rooted Wellness
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-semibold text-primary mb-4 sm:mb-6 tracking-tight leading-none" style={{ fontFamily: "'Righteous', cursive" }}>
              Lala Land
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary font-semibold mb-3 sm:mb-4">
              A blend of daily living with spiritual practices.
            </p>
            <p className="text-sm sm:text-base text-foreground/80 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
              Your whole-self sanctuary — woven into the rhythm of your real life. Daily affirmations, guided meditations, grounding rituals, nourishing recipes, a dream + vibe journal, a Soul Circle community, and tools to help you come home to yourself. Built for the culture. Built for every soul.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto justify-center">
              <Link to="/register" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-primary text-primary-foreground rounded-full px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-get-started-btn"
                >
                  Begin Your Journey
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full sm:w-auto rounded-full px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg border-primary/30 hover:bg-primary/10"
                  data-testid="hero-signin-btn"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Our Story Link */}
          <div className="mt-6 sm:mt-8">
            <Link to="/about" className="inline-flex items-center gap-2 text-primary/80 hover:text-primary transition-colors group bg-background/60 backdrop-blur-sm px-4 py-2 rounded-full">
              <span className="text-xs sm:text-sm font-medium">Discover Our Story</span>
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative pb-6 sm:pb-8 flex justify-center animate-bounce">
          <TreeDeciduous className="h-5 w-5 sm:h-6 sm:w-6 text-primary/50" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <p className="text-sm uppercase tracking-[0.2em] text-accent font-semibold mb-4">What We Offer</p>
            <h2 className="text-4xl md:text-5xl text-primary mb-4">Your Complete Wellness Journey</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Blend your daily living with spiritual practices — everything you need for holistic growth, mental wellness, and healing in one beautiful space.
            </p>
          </div>

          {/* Top 10 Feature Grid — refreshed Feb 2026 to match Lauren's
             priority list. Order intentionally mirrors how she communicates
             the app on social. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
            {/* 1. Community */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-violet-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center mb-3">
                <Users className="h-5 w-5 text-violet-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Community</h3>
              <p className="text-xs text-muted-foreground">Soul Circle — chat, posts, friends</p>
            </div>

            {/* 2. Readables Library */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-rose-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center mb-3">
                <Library className="h-5 w-5 text-rose-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Readables Library</h3>
              <p className="text-xs text-muted-foreground">410+ pages, audio, bookmarks</p>
            </div>

            {/* 3. Wellness & Lifestyle */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-green-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mb-3">
                <Salad className="h-5 w-5 text-green-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Wellness &amp; Lifestyle</h3>
              <p className="text-xs text-muted-foreground">Nutrition, cycle, hobbies, body</p>
            </div>

            {/* 4. Journal */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-pink-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center mb-3">
                <NotebookPen className="h-5 w-5 text-pink-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Journal</h3>
              <p className="text-xs text-muted-foreground">Reflect, track, release</p>
            </div>

            {/* 5. Meditations */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-indigo-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center mb-3">
                <Headphones className="h-5 w-5 text-indigo-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Meditations</h3>
              <p className="text-xs text-muted-foreground">Guided, AI-personalized</p>
            </div>

            {/* 6. The Game */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-3">
                <MessageSquare className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">The Game</h3>
              <p className="text-xs text-muted-foreground">Courageous Conversations</p>
            </div>

            {/* 7. Sound Sanctuary */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-violet-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center mb-3">
                <Music className="h-5 w-5 text-violet-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Sound Sanctuary</h3>
              <p className="text-xs text-muted-foreground">Healing frequencies, sound baths</p>
            </div>

            {/* 8. Affirmations */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                <Sparkles className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Affirmations</h3>
              <p className="text-xs text-muted-foreground">Daily mindset shifts</p>
            </div>

            {/* 9. Rooted Resources */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-rose-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center mb-3">
                <HandHeart className="h-5 w-5 text-rose-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Rooted Resources</h3>
              <p className="text-xs text-muted-foreground">Therapy finder + Black-owned brands</p>
            </div>

            {/* 10. Spiritual Awakenings */}
            <div className="group bg-card rounded-xl border border-border/50 p-4 hover:border-purple-500/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mb-3">
                <Sun className="h-5 w-5 text-purple-500" />
              </div>
              <h3 className="text-sm font-semibold text-primary mb-1">Spiritual Awakenings</h3>
              <p className="text-xs text-muted-foreground">Awakening guides, cosmic blueprint</p>
            </div>
          </div>

          {/* "And so much more" — kept short and enticing on purpose. Lauren's
             Feb 2026 note: don't list every feature here, that's overload and
             gives the secrets away. Tease, don't dump. */}
          <div className="max-w-2xl mx-auto text-center mb-12 px-4">
            <p className="text-sm uppercase tracking-[0.2em] text-accent font-semibold mb-3">…and so much more inside</p>
            <p className="text-muted-foreground leading-relaxed">
              From your daily rituals to the sacred surprises we don&rsquo;t spoil here — Lala Land is built to
              {" "}<span className="text-primary font-medium">meet you where you are</span> and grow with you.
              {" "}<span className="italic">Built by a Black woman, for a community that deserves a sanctuary that feels like home.</span> 🌳
            </p>
          </div>

          {/* Free Access Banner */}
          <div className="bg-gradient-to-r from-slate-500/10 to-zinc-500/10 rounded-2xl border border-slate-500/20 p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-primary mb-1">✨ Always Free Access</h3>
                <p className="text-sm text-muted-foreground">
                  Start your journey free with the Soul Circle Community, daily Affirmations, your Journal, and a taste of the Sound Sanctuary &amp; Spiritual Awakenings.
                </p>
              </div>
              <Link to="/login">
                <Button variant="outline" className="rounded-full whitespace-nowrap">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>

          {/* Membership Tiers */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Seedling - Free Tier */}
            <div className="group bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/30 p-6 hover:border-green-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <span className="text-green-500 font-bold text-lg">Seedling</span>
                <div className="flex items-baseline justify-center gap-1 mt-2">
                  <span className="text-3xl font-bold text-primary">FREE</span>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Soul Circle Community access</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Daily Affirmations &amp; Vibe Check</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Journal (10 entries / month)</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> 3 Sound Sanctuary tracks</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Spiritual Awakenings starter</li>
              </ul>
              <Link to="/register">
                <Button className="w-full rounded-full bg-green-500 hover:bg-green-600 text-white">
                  Get Started Free
                </Button>
              </Link>
            </div>

            {/* Rooted Tier - Featured */}
            <div className="group bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl border-2 border-violet-500/50 p-6 relative transform md:-translate-y-4 shadow-xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                Most Popular
              </div>
              <div className="text-center mb-4">
                <span className="text-violet-500 font-bold text-lg">Rooted</span>
                <div className="flex items-baseline justify-center gap-1 mt-2">
                  <span className="text-3xl font-bold text-primary">$5.55</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">or $44.44/year</p>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> Full Readables Library + Audio</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> Wellness &amp; Lifestyle (AI meal plans)</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> Personalized Meditations</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> The Game — Courageous Conversations</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> Full Sound Sanctuary &amp; Vibe Out Hub</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-violet-500" /> Rooted Resources + Manifestation Deck</li>
              </ul>
              <Link to="/subscription">
                <Button className="w-full rounded-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white">
                  Start Rooted Journey
                </Button>
              </Link>
            </div>

            {/* Forever Rooted - Lifetime */}
            <div className="group bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl border border-amber-500/30 p-6 hover:border-amber-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <span className="text-amber-500 font-bold text-lg">Forever Rooted</span>
                <div className="flex items-baseline justify-center gap-1 mt-2">
                  <span className="text-3xl font-bold text-primary">$111.11</span>
                  <span className="text-muted-foreground">once</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Lifetime Access</p>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-500" /> Everything in Rooted, Forever</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-500" /> All Future Features Included</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-500" /> Founding Member Badge</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-500" /> Early Access to New Content</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-500" /> Name in App Credits & more...</li>
              </ul>
              <Link to="/subscription">
                <Button className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                  Get Rooted Forever
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-muted/50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl text-primary mb-6">Ready to Transform?</h2>
          <p className="text-muted-foreground text-lg mb-10">
            Join our community of kings and queens who have found peace, clarity, and spiritual balance while healing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/subscription">
              <Button 
                size="lg" 
                className="bg-primary text-primary-foreground rounded-full px-12 py-6 text-lg hover:bg-primary/90 shadow-lg"
                data-testid="cta-get-started-btn"
              >
                View Membership Options
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                variant="outline"
                size="lg" 
                className="rounded-full px-12 py-6 text-lg border-primary/30 hover:bg-primary/5"
                data-testid="cta-register-btn"
              >
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-accent font-semibold mb-4">Community Voices</p>
            <h2 className="text-4xl md:text-5xl text-primary mb-4">Healing the Community Together</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Real stories from brothers and sisters on their wellness journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 - Man */}
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xl">
                  👨🏾
                </div>
                <div>
                  <p className="font-semibold text-primary">Marcus J.</p>
                  <p className="text-xs text-muted-foreground">Atlanta, GA</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "As a Black man, I never thought I'd be into 'wellness apps' but Lala Land changed that. The Men's Health tracking, the meditations, even the vibe checks — it all helps me stay grounded. My anxiety is way more manageable now."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>

            {/* Testimonial 2 - Woman */}
            <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-2xl p-6 border border-pink-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-xl">
                  👩🏾
                </div>
                <div>
                  <p className="font-semibold text-primary">Jasmine T.</p>
                  <p className="text-xs text-muted-foreground">Houston, TX</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "The symptom journal and cycle tracking have been a game-changer for understanding my body. Plus, my husband and I do the dream journal together now — it's become our thing. We're healing together! 💜"
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>

            {/* Testimonial 3 - Couple */}
            <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-2xl p-6 border border-violet-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xl">
                  💑🏾
                </div>
                <div>
                  <p className="font-semibold text-primary">DeShawn & Keisha</p>
                  <p className="text-xs text-muted-foreground">Chicago, IL</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "We use Lala Land as part of our morning routine. The affirmations, the miracle deck, the Soul Circle community — it keeps us spiritually aligned as a couple. Black love + Black wellness = powerful."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>

            {/* Testimonial 4 - Man */}
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-xl">
                  👨🏿
                </div>
                <div>
                  <p className="font-semibold text-primary">Kwame D.</p>
                  <p className="text-xs text-muted-foreground">Oakland, CA</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "The sound baths and meditations hit different. I'm a father of three and running a business — Lala Land gives me the tools to decompress. The Rooted Resources for Black therapists was a blessing too."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>

            {/* Testimonial 5 - Woman */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl p-6 border border-cyan-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xl">
                  👩🏿
                </div>
                <div>
                  <p className="font-semibold text-primary">Aaliyah M.</p>
                  <p className="text-xs text-muted-foreground">Brooklyn, NY</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "The nutrition hub with Ayurvedic recipes based on my dosha? Chef's kiss. And the Soul Circle community is so supportive. It feels like family. Finally, an app that gets us."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>

            {/* Testimonial 6 - Man */}
            <div className="bg-gradient-to-br from-indigo-500/10 to-slate-500/10 rounded-2xl p-6 border border-indigo-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-slate-600 flex items-center justify-center text-white text-xl">
                  👨🏾‍🦱
                </div>
                <div>
                  <p className="font-semibold text-primary">Terrence W.</p>
                  <p className="text-xs text-muted-foreground">Memphis, TN</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm italic mb-4">
                "I started using this after my therapist recommended tracking my moods. The vibe check feature is simple but powerful. I can see my patterns now. This is what healing looks like for Black men."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-background border-t border-border/50">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          {/* Logo - Dark mode consistent */}
          <Link to="/" className="flex items-center justify-center">
            {/* Light Mode: Show Logo */}
            {!isDark && (
              <img 
                src="https://customer-assets.emergentagent.com/job_4d1c54ed-139e-41a2-9c35-4d75dfddc66c/artifacts/cxzvcxai_Subheading.png" 
                alt="Rooted Wellness Lala Land Logo" 
                className="h-20 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            )}
            {/* Dark Mode: Glowing Leaf with Text */}
            {isDark && (
              <div className="flex items-center gap-3 group">
                <div className="relative">
                  <Leaf 
                    className="h-14 w-14 text-emerald-400 transition-all duration-500 group-hover:scale-110"
                    style={{
                      filter: 'drop-shadow(0 0 10px rgba(52, 211, 153, 0.8)) drop-shadow(0 0 20px rgba(52, 211, 153, 0.6)) drop-shadow(0 0 30px rgba(52, 211, 153, 0.4))'
                    }}
                  />
                  <div className="absolute inset-0 bg-emerald-400/40 blur-xl rounded-full animate-pulse"></div>
                </div>
                <span 
                  className="text-2xl font-semibold text-white tracking-wide"
                  style={{
                    textShadow: '0 0 10px rgba(52, 211, 153, 0.6), 0 0 20px rgba(52, 211, 153, 0.4)'
                  }}
                >
                  Lala Land
                </span>
              </div>
            )}
          </Link>
          
          {/* Stay Rooted */}
          <p className="text-muted-foreground text-sm italic">Stay Rooted 🌳</p>

          {/* Install App Section */}
          <div className="w-full max-w-2xl mx-auto my-8 p-6 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 rounded-2xl border border-emerald-500/20">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Smartphone className="h-6 w-6 text-emerald-500" />
              <h3 className="text-lg font-bold text-primary">Get the App</h3>
            </div>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Install Lala Land on your phone for the best experience — access your wellness journey anytime, anywhere!
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* iOS Instructions */}
              <div className="bg-background/60 rounded-xl p-4 border border-primary/10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  </div>
                  <span className="font-semibold text-primary text-sm">iPhone / iPad</span>
                </div>
                <ol className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                    <span>Open this site in <span className="font-medium text-primary">Safari</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                    <span>Tap <Share className="h-3 w-3 inline mx-1" /> Share button at bottom</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                    <span>Tap <Plus className="h-3 w-3 inline mx-1" /> "Add to Home Screen"</span>
                  </li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="bg-background/60 rounded-xl p-4 border border-primary/10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
                    </svg>
                  </div>
                  <span className="font-semibold text-primary text-sm">Android</span>
                </div>
                <ol className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                    <span>Open this site in <span className="font-medium text-primary">Chrome</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                    <span>Tap <MoreVertical className="h-3 w-3 inline mx-1" /> menu (top right)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                    <span>Tap "Add to Home Screen" or "Install"</span>
                  </li>
                </ol>
              </div>

              {/* Google Phone (Pixel) Instructions */}
              <div className="bg-background/60 rounded-xl p-4 border border-primary/10">
                <div className="flex items-center gap-2 mb-3">
                  {/* Google "G" mark — uses Google's four colors so Pixel users self-identify */}
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M21.35 11.1H12v3.2h5.35c-.5 2.5-2.6 4-5.35 4-3.2 0-5.8-2.6-5.8-5.8s2.6-5.8 5.8-5.8c1.45 0 2.75.5 3.8 1.5l2.4-2.4C16.5 4.3 14.4 3.4 12 3.4c-4.75 0-8.6 3.85-8.6 8.6s3.85 8.6 8.6 8.6c4.95 0 8.25-3.45 8.25-8.35 0-.55-.05-1-.15-1.45z"/>
                      <path fill="#34A853" d="M5.27 14.3l2.65-1.95c-.7-1.6-.7-3.4 0-5L5.27 5.4c-1.35 2.7-1.35 5.85 0 8.55z" opacity="0"/>
                    </svg>
                  </div>
                  <span className="font-semibold text-primary text-sm">Google Phone (Pixel)</span>
                </div>
                <ol className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                    <span>Open this site in <span className="font-medium text-primary">Google Chrome</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                    <span>Tap <MoreVertical className="h-3 w-3 inline mx-1" /> menu (top right)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                    <span>Tap <span className="font-medium text-primary">"Install app"</span> — Lala Land joins your Pixel home screen</span>
                  </li>
                </ol>
              </div>
            </div>

            <p className="text-xs text-center text-muted-foreground mt-4">
              ✨ Full screen experience • ⚡ Instant access • 📱 Works like a native app
            </p>
          </div>
          
          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
              Our Story
            </Link>
            <Link to="/subscription" className="text-muted-foreground hover:text-primary transition-colors">
              Membership
            </Link>
            <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Contact Us
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div>
          
          {/* Bottom Row */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 w-full pt-4 border-t border-border/30">
            <span className={`text-lg font-semibold ${isDark ? 'text-emerald-400' : 'text-primary'}`} style={{ fontFamily: "'Righteous', cursive" }}>
              Lala Land by Rooted Wellness
            </span>
            <p className="text-muted-foreground text-sm">
              © 2024 Rooted Wellness. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
